/* eslint-disable @typescript-eslint/no-empty-function */
import { CommonModule } from '@angular/common';
import {
  Component,
  EventEmitter,
  forwardRef,
  HostBinding,
  Input,
  Output
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { UmssLabelComponent } from '../label/label.component';
import { UmssSwitchComponent } from '../switch/switch.component';

@Component({
  selector: 'umss-rich-switch-group',
  standalone: true,
  imports: [CommonModule, UmssSwitchComponent, UmssLabelComponent],
  templateUrl: './rich-switch-group.component.html',
  styleUrls: ['./rich-switch-group.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => UmssRichSwitchGroupComponent),
      multi: true
    }
  ]
})
export class UmssRichSwitchGroupComponent implements ControlValueAccessor {
  @HostBinding('style.display') display = 'contents';

  /** Content props */
  @Input() label: string = 'Label';
  @Input() secondaryText: string = 'Secondary text';
  @Input() ariaLabel?: string;

  /** Variant props */
  @Input() size: 'Small' | 'Regular' | 'Large' = 'Regular';

  /** State props */
  @Input() disabled: boolean = false;
  @Input() required: boolean = false;

  /** Value prop */
  private _checked: boolean = false;
  @Input() set checked(val: boolean) {
    this._checked = val;
    this._onChange(val);
  }
  get checked(): boolean {
    return this._checked;
  }

  /** Event outputs */
  @Output() checkedChange = new EventEmitter<boolean>();
  @Output() change = new EventEmitter<boolean>();

  /** ControlValueAccessor implementation */
  private _onChange: (value: boolean) => void = () => {};
  private _onTouched: () => void = () => {};

  writeValue(value: boolean): void {
    this._checked = value;
  }

  registerOnChange(fn: (value: boolean) => void): void {
    this._onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this._onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  /** Event handlers */
  onSwitchChange(value: boolean): void {
    if (this.disabled) return;

    this._checked = value;
    this._onChange(value);
    this._onTouched();
    this.checkedChange.emit(value);
    this.change.emit(value);
  }

  /** Computed properties */
  get computedAriaLabel(): string {
    return this.ariaLabel || this.label || 'Toggle switch';
  }
}
